import React from "react";
import {cn} from "../../utils/cn";

const toneAccent = {
  brand: "text-brand-700",
  success: "text-success-600",
  danger: "text-danger-600",
  warning: "text-warning-600",
  info: "text-info-600",
  neutral: "text-ink-500",
};

/**
 * Clickable KPI tile. Acts as a filter button — `active` highlights it.
 *
 * Affordances:
 *   - Hovering an interactive tile shows a "Filter by …" tooltip so the
 *     click-to-filter behaviour is discoverable before the first click.
 *   - A zero-count tile is not clickable (and renders muted): filtering
 *     into a guaranteed-empty table reads like a bug. If it is already
 *     active it stays clickable so the filter can always be cleared.
 *
 *   <KpiTile label="Active" value={6} icon={CheckCircle} tone="success" active={…} onClick={…} delta="+1 since yesterday"/>
 */
export default function KpiTile({label, value, icon: Icon, tone = "neutral", active = false, delta, onClick, className, ...props}) {
  const isEmpty = value === 0 || value === undefined || value === null;
  const interactive = !!onClick && (!isEmpty || active);
  const Comp = interactive ? "button" : "div";

  return (
    <Comp
      type={interactive ? "button" : undefined}
      aria-current={interactive ? active : undefined}
      onClick={interactive ? onClick : undefined}
      title={
        interactive
          ? (active ? `Showing ${label} — click to clear the filter` : `Filter by ${label}`)
          : (onClick ? `No records — nothing to filter` : undefined)
      }
      className={cn(
        // Compact summary-bar tile (space-optimization pass): the strip
        // must read as one short row, not a hero card grid.
        "flex-1 min-w-[120px] text-left bg-white rounded-lg border px-3 py-2 shadow-xs",
        "transition-all duration-fast",
        interactive && "cursor-pointer hover:shadow-sm hover:border-ink-300 focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-2",
        onClick && !interactive && "opacity-60",
        active ? "border-brand-500 bg-brand-50/40 ring ring-offset-0" : "border-ink-200",
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-2xs uppercase tracking-wider font-semibold text-ink-500 truncate">{label}</p>
        {Icon && <Icon className={cn("h-3.5 w-3.5 shrink-0", toneAccent[tone])} aria-hidden="true" />}
      </div>
      <p className="text-xl font-bold text-ink-900 mt-0.5 tabular-nums leading-tight">
        {value === undefined || value === null ? "—" : value}
      </p>
      {delta && <p className="text-xs text-ink-500 mt-0.5 truncate">{delta}</p>}
    </Comp>
  );
}

/**
 * Single-row summary strip: tiles never wrap into a second row — on
 * narrow screens the strip scrolls horizontally instead of pushing the
 * table below the fold.
 */
export function KpiRow({className, children}) {
  return (
    <div
      className={cn(
        // py-1: the strip is a scroll container, so without vertical
        // padding it clips the active tile's ring/shadow at the top.
        "flex items-stretch gap-2 -mx-1 px-1 py-1",
        "overflow-x-auto flex-nowrap",
        "[&>*]:flex-shrink-0 sm:[&>*]:flex-shrink",
        "[&>*]:min-w-[120px]",
        className,
      )}
    >
      {children}
    </div>
  );
}
